import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Play, Pause, ChevronLeft, ChevronRight, X } from 'lucide-react';

/**
 * BurningTeam Theme-Song Mini-Player
 *
 * Verhalten:
 * - Beim 1. Scroll (oder Klick/Touch) auf Landingpage: Panel ploppt aus + Fade-In-Play
 * - Auf /login, /register, /auth: Fade-Out + automatisch pausieren (nicht dismissed)
 * - User kann manuell wieder auf Play drücken → endlos-Loop
 * - Lautstärke: 60 %, Loop: true
 * - Floating unten rechts; ausgefahren: Play/Pause · Spotify · Collapse · X
 * - X = dauerhaft ausblenden (localStorage); in Settings reaktivierbar
 */

const DISMISS_KEY = 'bt_music_dismissed';
const EXPANDED_KEY = 'bt_music_expanded';
const FIRST_POP_KEY = 'bt_music_first_pop_shown';
const SPOTIFY_URL = 'https://open.spotify.com/artist/1QZT1gtEmm45U8PJmHFsBN?si=Fnc3en0wQ5ydwKgPXA475Q';
const TARGET_VOLUME = 0.13;
const QUIET_ROUTES = [/^\/login/i, /^\/register/i, /^\/auth/i];
// Routen, auf denen die Musik laufen DARF (öffentliche Seiten inkl. Anmelden/Registrieren)
const MUSIC_ROUTES = [
  /^\/$/,
  /^\/login/i,
  /^\/register/i,
  /^\/verify-email/i,
  /^\/forgot-password/i,
  /^\/reset-password/i,
  /^\/support/i,
];

export default function ThemeMusicPlayer() {
  const location = useLocation();
  const [dismissed, setDismissed] = useState(() =>
    typeof window !== 'undefined' && localStorage.getItem(DISMISS_KEY) === '1'
  );
  const [expanded, setExpanded] = useState(() =>
    typeof window !== 'undefined' && localStorage.getItem(EXPANDED_KEY) !== '0'
  );
  const [isPlaying, setIsPlaying] = useState(false);
  // User hat manuell auf Play gedrückt → Musik läuft auf allen Seiten durch, kein Auto-Fade-Out mehr
  const [manuallyStarted, setManuallyStarted] = useState(false);
  const audioRef = useRef(null);
  const audioCtxRef = useRef(null);
  const gainNodeRef = useRef(null);
  const triedAutoplay = useRef(false);
  const fadeTimer = useRef(null);

  // Settings-Toggle Listener
  useEffect(() => {
    const handler = () => setDismissed(localStorage.getItem(DISMISS_KEY) === '1');
    window.addEventListener('bt-music-visibility-change', handler);
    return () => window.removeEventListener('bt-music-visibility-change', handler);
  }, []);

  // Audio & AudioContext werden LAZY beim ersten Play-Trigger angelegt (nicht beim Mount),
  // damit die Landingpage nicht 3,3 MB beim Laden vorzieht.
  const initAudio = () => {
    if (audioRef.current) return; // bereits initialisiert
    const audio = new Audio('/burningteam-theme.mp3');
    audio.loop = true;
    audio.crossOrigin = 'anonymous';
    audio.preload = 'metadata';
    audioRef.current = audio;

    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      const source = ctx.createMediaElementSource(audio);
      const gain = ctx.createGain();
      gain.gain.value = 0;
      source.connect(gain);
      gain.connect(ctx.destination);
      audioCtxRef.current = ctx;
      gainNodeRef.current = gain;
    } catch (e) {
      audio.volume = 0;
    }

    audio.addEventListener('play', () => setIsPlaying(true));
    audio.addEventListener('pause', () => setIsPlaying(false));
  };

  // Cleanup on unmount/dismiss
  useEffect(() => {
    return () => {
      if (fadeTimer.current) clearInterval(fadeTimer.current);
      if (audioRef.current) audioRef.current.pause();
      if (audioCtxRef.current) {
        try { audioCtxRef.current.close(); } catch {}
        audioCtxRef.current = null;
      }
      gainNodeRef.current = null;
      audioRef.current = null;
    };
  }, []);

  // Pause & cleanup wenn user den Player dismissed
  useEffect(() => {
    if (dismissed && audioRef.current) {
      audioRef.current.pause();
    }
  }, [dismissed]);

  /** Aktuelle Lautstärke lesen (bevorzugt Gain-Node) */
  const getCurrentVolume = () => {
    if (gainNodeRef.current) return gainNodeRef.current.gain.value;
    return audioRef.current ? audioRef.current.volume : 0;
  };

  /** Lautstärke setzen (bevorzugt Gain-Node) */
  const setCurrentVolume = (v) => {
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = v;
    } else if (audioRef.current) {
      audioRef.current.volume = v;
    }
  };

  /** Sanftes Fade-In auf TARGET_VOLUME in ~1.4s */
  const fadeIn = () => {
    if (!audioRef.current) return;
    if (fadeTimer.current) clearInterval(fadeTimer.current);
    // AudioContext auf User-Interaktion hin "entsperren"
    if (audioCtxRef.current && audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume().catch(() => {});
    }
    const step = 0.015;
    fadeTimer.current = setInterval(() => {
      if (!audioRef.current) return;
      const next = Math.min(TARGET_VOLUME, getCurrentVolume() + step);
      setCurrentVolume(next);
      if (next >= TARGET_VOLUME) clearInterval(fadeTimer.current);
    }, 60);
  };

  /** Sanftes Fade-Out & danach pausieren */
  const fadeOutAndPause = () => {
    if (!audioRef.current) return;
    if (fadeTimer.current) clearInterval(fadeTimer.current);
    fadeTimer.current = setInterval(() => {
      if (!audioRef.current) return;
      const next = Math.max(0, getCurrentVolume() - 0.015);
      setCurrentVolume(next);
      if (next <= 0) {
        clearInterval(fadeTimer.current);
        audioRef.current.pause();
        setCurrentVolume(0); // für nächsten Play wieder 0 als Startwert
      }
    }, 60);
  };

  /** Panel ausfahren + Play triggern (nur wenn Musik auf dieser Route erlaubt ist) */
  const popOutAndPlay = useCallback(() => {
    if (dismissed || triedAutoplay.current) return;
    const musicAllowed = MUSIC_ROUTES.some(r => r.test(location.pathname));
    if (!musicAllowed) return;
    initAudio();
    if (!audioRef.current) return;
    // Panel nur ausfahren, wenn der User es noch NIE manuell eingeklappt hat
    // (beim allerersten Pop-Out zeigen, danach User-Präferenz respektieren)
    if (localStorage.getItem(EXPANDED_KEY) === null) {
      setExpanded(true);
      localStorage.setItem(EXPANDED_KEY, '1');
    }
    setCurrentVolume(0);
    if (audioCtxRef.current && audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume().catch(() => {});
    }
    audioRef.current.play()
      .then(() => {
        triedAutoplay.current = true;
        fadeIn();
      })
      .catch(() => { /* Browser hat blockiert */ });
  }, [dismissed, location.pathname]);

  // Auto-Start bei jeder User-Interaktion solange Musik noch nicht läuft
  useEffect(() => {
    if (dismissed) return;
    const events = ['scroll', 'click', 'touchstart', 'keydown', 'pointerdown'];
    events.forEach(ev => window.addEventListener(ev, popOutAndPlay, { passive: true }));
    return () => events.forEach(ev => window.removeEventListener(ev, popOutAndPlay));
  }, [dismissed, popOutAndPlay]);

  // Route-Wechsel: wenn Musik NICHT erlaubt auf dieser Route → Fade-Out + Pause
  // ABER: wenn User manuell Play gedrückt hat (manuallyStarted), Musik auf allen Seiten laufen lassen
  useEffect(() => {
    if (dismissed || !audioRef.current || manuallyStarted) return;
    const musicAllowed = MUSIC_ROUTES.some(r => r.test(location.pathname));
    if (!musicAllowed && !audioRef.current.paused) {
      fadeOutAndPause();
    }
  }, [location.pathname, dismissed, manuallyStarted]);

  const togglePlay = () => {
    initAudio();
    if (!audioRef.current) return;
    if (audioRef.current.paused) {
      setCurrentVolume(0);
      if (audioCtxRef.current && audioCtxRef.current.state === 'suspended') {
        audioCtxRef.current.resume().catch(() => {});
      }
      audioRef.current.play()
        .then(() => {
          setManuallyStarted(true);
          fadeIn();
        })
        .catch(() => {/* ignore */});
    } else {
      setManuallyStarted(false);
      fadeOutAndPause();
    }
  };

  const toggleExpand = () => {
    setExpanded(prev => {
      const next = !prev;
      localStorage.setItem(EXPANDED_KEY, next ? '1' : '0');
      return next;
    });
  };

  const handleDismiss = () => {
    if (audioRef.current) audioRef.current.pause();
    localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  if (dismissed) return null;

  return (
    <div
      data-testid="theme-music-player"
      className="fixed bottom-3 right-3 z-[60] select-none"
      style={{ WebkitTapHighlightColor: 'transparent' }}
    >
      {expanded ? (
        <div
          className="flex items-center gap-1 bg-slate-900/90 backdrop-blur-sm text-white rounded-full shadow-2xl border border-slate-700 pl-1 pr-1 py-1 transition-all duration-300"
          style={{
            animation: (() => {
              const first = localStorage.getItem(FIRST_POP_KEY) !== '1';
              if (first) {
                // Ersten Pop-out merken (leicht verzögert damit Animation einmal abläuft)
                setTimeout(() => localStorage.setItem(FIRST_POP_KEY, '1'), 1400);
                return 'bt-music-popout-bounce 1200ms cubic-bezier(0.22, 1.2, 0.36, 1)';
              }
              return 'bt-music-popout 420ms cubic-bezier(0.22, 1.2, 0.36, 1)';
            })(),
          }}
        >
          <style>{`
            @keyframes bt-music-popout {
              0%   { opacity: 0; transform: translateX(40px) scale(0.6); }
              60%  { opacity: 1; transform: translateX(-4px) scale(1.03); }
              100% { opacity: 1; transform: translateX(0) scale(1); }
            }
            @keyframes bt-music-popout-bounce {
              0%   { opacity: 0; transform: translateX(60px) scale(0.5) rotate(-6deg); }
              18%  { opacity: 1; transform: translateX(-12px) scale(1.08) rotate(3deg); }
              32%  { transform: translateX(8px) scale(0.96) rotate(-2deg); }
              46%  { transform: translateX(-6px) scale(1.04) rotate(1.5deg); }
              60%  { transform: translateX(4px) scale(0.99) rotate(-1deg); }
              74%  { transform: translateX(-2px) scale(1.01) rotate(0.5deg); }
              88%  { transform: translateX(1px) scale(1) rotate(0deg); }
              100% { opacity: 1; transform: translateX(0) scale(1) rotate(0); }
            }
          `}</style>
          {/* Play/Pause */}
          <button
            type="button"
            onClick={togglePlay}
            data-testid="theme-music-playpause-btn"
            aria-label={isPlaying ? 'Pause' : 'Play'}
            className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
              isPlaying
                ? 'bg-emerald-500 hover:bg-emerald-600'
                : 'bg-orange-500 hover:bg-orange-600'
            }`}
          >
            {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 translate-x-[1px]" />}
          </button>

          {/* Spotify */}
          <a
            href={SPOTIFY_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="theme-music-spotify-link"
            aria-label="Auf Spotify öffnen"
            className="w-9 h-9 rounded-full bg-[#1DB954] hover:bg-[#1ed760] flex items-center justify-center transition-colors"
            title="Artist auf Spotify"
          >
            <svg viewBox="0 0 168 168" className="w-[18px] h-[18px]" aria-hidden="true">
              <path fill="#fff" d="M83.996.277C37.747.277.253 37.77.253 84.019c0 46.251 37.494 83.741 83.743 83.741 46.254 0 83.744-37.49 83.744-83.741 0-46.246-37.49-83.738-83.745-83.738zm38.404 120.78a5.217 5.217 0 01-7.18 1.73c-19.662-12.01-44.414-14.73-73.564-8.07a5.222 5.222 0 01-6.249-3.93 5.213 5.213 0 013.926-6.25c31.9-7.29 59.263-4.15 81.337 9.34 2.46 1.51 3.24 4.72 1.73 7.18zm10.25-22.805c-1.89 3.075-5.91 4.045-8.98 2.155-22.51-13.839-56.823-17.846-83.448-9.764-3.453 1.043-7.1-.903-8.148-4.35-1.04-3.453.907-7.093 4.354-8.143 30.413-9.228 68.222-4.758 94.072 11.127 3.07 1.89 4.04 5.91 2.15 8.976zm.88-23.744c-26.99-16.031-71.52-17.505-97.289-9.684-4.138 1.255-8.514-1.081-9.768-5.219a7.835 7.835 0 015.221-9.771c29.581-8.98 78.756-7.245 109.83 11.202a7.823 7.823 0 012.74 10.733c-2.2 3.722-7.02 4.949-10.73 2.739z"/>
            </svg>
          </a>

          {/* Collapse-Pfeil */}
          <button
            type="button"
            onClick={toggleExpand}
            data-testid="theme-music-collapse-btn"
            aria-label="Einklappen"
            className="w-9 h-9 rounded-full bg-slate-700 hover:bg-slate-600 flex items-center justify-center transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>

          {/* X – dauerhaft ausblenden */}
          <button
            type="button"
            onClick={handleDismiss}
            data-testid="theme-music-dismiss-btn"
            aria-label="Dauerhaft ausblenden"
            className="w-8 h-8 rounded-full hover:bg-slate-700 flex items-center justify-center text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={toggleExpand}
          data-testid="theme-music-expand-btn"
          aria-label="Musik-Panel öffnen"
          className="w-8 h-8 rounded-full bg-slate-900/80 backdrop-blur-sm text-white shadow-lg border border-slate-700 flex items-center justify-center hover:bg-slate-800 transition-all relative"
        >
          <ChevronLeft className="w-4 h-4" />
          {isPlaying && (
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
          )}
        </button>
      )}
    </div>
  );
}
